import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  socket,
  joinRoom,
  leaveRoom,
  onDeploymentStarted,
  onDeploymentProgress,
  onDeploymentCompleted,
  onDeploymentFailed,
  onDeploymentError,
  onDeploymentCancelled,
  offDeploymentStarted,
  offDeploymentProgress,
  offDeploymentCompleted,
  offDeploymentFailed,
  offDeploymentError,
  offDeploymentCancelled,
} from "../services/socket";
import { updateDeploymentStatus } from "../redux/slices/gitSlice";
import { updateDeploymentInList } from "../redux/slices/deploymentSlice";

/**
 * Custom hook for handling real-time deployment updates via Socket.io
 * @param {string} siteId - Site ID to listen for deployments
 * @param {function} onMessage - Callback for displaying messages to user
 * @returns {boolean} - Socket connection status
 */
export const useDeploymentSocket = (siteId, onMessage) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!siteId) return;

    // Join site-specific room
    const room = `user_site_${siteId}`;
    joinRoom(room);

    // Deployment started handler
    const handleDeploymentStarted = (data) => {
      if (onMessage) {
        onMessage(`Deployment started for ${data.branch || "repository"}`);
      }

      dispatch(
        updateDeploymentStatus({
          deploymentId: data.deploymentId,
          status: "CLONING",
          message: data.message,
        }),
      );

      dispatch(
        updateDeploymentInList({
          deploymentId: data.deploymentId,
          updates: {
            status: "CLONING",
            message: data.message,
          },
        }),
      );
    };

    // Deployment progress handler
    const handleDeploymentProgress = (data) => {
      dispatch(
        updateDeploymentStatus({
          deploymentId: data.deploymentId,
          status: data.status,
          message: data.message,
        }),
      );

      dispatch(
        updateDeploymentInList({
          deploymentId: data.deploymentId,
          updates: {
            status: data.status,
            message: data.message,
          },
        }),
      );
    };

    // Deployment completed handler
    const handleDeploymentCompleted = (data) => {
      if (onMessage) {
        onMessage(
          `Deployment completed successfully in ${data.duration || "N/A"}`,
        );
      }

      // Refresh deployment list to get updated deployment details
      if (siteId && data.deploymentId) {
        import("../redux/slices/gitSlice").then(
          ({ fetchDeploymentsBySite }) => {
            dispatch(fetchDeploymentsBySite({ siteId, params: { limit: 50 } }));
          },
        );
      }

      dispatch(
        updateDeploymentStatus({
          deploymentId: data.deploymentId,
          status: "SUCCESS",
          message: data.message,
        }),
      );

      dispatch(
        updateDeploymentInList({
          deploymentId: data.deploymentId,
          updates: {
            status: "SUCCESS",
            completedAt: new Date().toISOString(),
            message: data.message,
          },
        }),
      );
    };

    // Deployment failed handler
    const handleDeploymentFailed = (data) => {
      if (onMessage) {
        onMessage(`Deployment failed: ${data.error || "Unknown error"}`);
      }

      // Refresh deployment list and queue to get updated details
      if (siteId && data.deploymentId) {
        import("../redux/slices/gitSlice").then(
          ({ fetchDeploymentsBySite, fetchDeploymentQueue }) => {
            dispatch(fetchDeploymentsBySite({ siteId, params: { limit: 50 } }));
            dispatch(fetchDeploymentQueue(siteId));
          },
        );
      }

      dispatch(
        updateDeploymentStatus({
          deploymentId: data.deploymentId,
          status: "FAILED",
          message: data.message || data.error,
        }),
      );

      dispatch(
        updateDeploymentInList({
          deploymentId: data.deploymentId,
          updates: {
            status: "FAILED",
            completedAt: new Date().toISOString(),
            errorMessage: data.error,
            message: data.message,
          },
        }),
      );
    };

    // Deployment error handler
    const handleDeploymentError = (data) => {
      if (onMessage) {
        onMessage(`Deployment error: ${data.error || "Unknown error"}`);
      }

      dispatch(
        updateDeploymentInList({
          deploymentId: data.deploymentId,
          updates: {
            errorMessage: data.error,
          },
        }),
      );
    };

    // Deployment cancelled handler
    const handleDeploymentCancelled = (data) => {
      if (onMessage) {
        onMessage(`Deployment cancelled`);
      }

      dispatch(
        updateDeploymentStatus({
          deploymentId: data.deploymentId,
          status: "CANCELLED",
          message: "Deployment was cancelled",
        }),
      );

      dispatch(
        updateDeploymentInList({
          deploymentId: data.deploymentId,
          updates: {
            status: "CANCELLED",
            completedAt: new Date().toISOString(),
          },
        }),
      );
    };

    // Register event listeners
    onDeploymentStarted(handleDeploymentStarted);
    onDeploymentProgress(handleDeploymentProgress);
    onDeploymentCompleted(handleDeploymentCompleted);
    onDeploymentFailed(handleDeploymentFailed);
    onDeploymentError(handleDeploymentError);
    onDeploymentCancelled(handleDeploymentCancelled);

    // Cleanup on unmount
    return () => {
      leaveRoom(room);
      offDeploymentStarted(handleDeploymentStarted);
      offDeploymentProgress(handleDeploymentProgress);
      offDeploymentCompleted(handleDeploymentCompleted);
      offDeploymentFailed(handleDeploymentFailed);
      offDeploymentError(handleDeploymentError);
      offDeploymentCancelled(handleDeploymentCancelled);
    };
  }, [siteId, dispatch, onMessage]);

  return socket.connected;
};

export default useDeploymentSocket;
